import { Box } from "@chakra-ui/react"
import * as React from "react"
import { RiRecordCircleFill } from "react-icons/ri"
import { useRecoilValue, useRecoilState } from "recoil"
import { Icon } from "../../common/icons"
import { SDKContext } from "../../../context/SDKContext"
import LocalButton from "./_localButton"
import {
  connectBotRecordingAtom,
  recordingLoaderAtom,
  recordingStartAtom,
  recordingUserIdAtom
} from "../../../recoil/atoms/recording"
import { useTranslation } from "react-i18next"
import { streamsStartAtom } from "../../../recoil/atoms/streams"
import { Tooltip } from "../../common/tooltip"
import LocalButtonLoader from "./LocalButtonLoader"

const UserPermission = window.MeetOnline?.UserPermission

const RecordButton = ({ isWithoutTitle, localUserProperties, isFirst = false, isLast = false }) => {
  const { t } = useTranslation()

  const [isRecordingLoader, setRecordingLoader] =
    useRecoilState<boolean>(recordingLoaderAtom)
  const [isConnectRecordingBot, setConnectRecordingBot] =
    useRecoilState<boolean>(connectBotRecordingAtom)
  const [isRecordingStart, setRecordingStart] =
    useRecoilState<boolean>(recordingStartAtom)
  const [isDisconnectRecordingBot, setDisconnectRecordingBot] =
    React.useState(false)
  const isStreamingStarted = useRecoilValue(streamsStartAtom)

  const recordingUserId = useRecoilValue(recordingUserIdAtom)

  const meetingClient = React.useContext(SDKContext)

  const isButtonHidden = React.useCallback(
    (p: number) => {
      return (localUserProperties?.permissions & p) === 0
    },
    [localUserProperties?.permissions]
  )

  const handleToggleRecord = React.useCallback(() => {
    setRecordingLoader(true)
    if (isRecordingStart) {
      setConnectRecordingBot(false)
      setDisconnectRecordingBot(true)
      meetingClient?.toggleRecord("stop")
    } else {
      meetingClient?.toggleRecord("start")
      setRecordingStart(true)
    }
  }, [
    isRecordingStart,
    meetingClient,
    setConnectRecordingBot,
    setRecordingLoader,
    setRecordingStart
  ])

  // is local user is not owner of recording
  const isNonRecordingUser = isRecordingStart && recordingUserId !== localUserProperties?.localUserId

  const isRecordingDisabled = React.useMemo(() => {
    // If streaming is active, disable the record button
    if (isStreamingStarted) return true

    if (isNonRecordingUser) {
      // Some other user has start the recording
      return true
    }

    return isButtonHidden(UserPermission.canRecordSessions)
  }, [isStreamingStarted, isButtonHidden, isNonRecordingUser])

  React.useEffect(() => {
    if (isConnectRecordingBot) {
      let timer = setTimeout(() => {
        setRecordingLoader(false)
        setRecordingStart(true)
      }, 3000)
      return () => clearTimeout(timer)
    }

    if (isDisconnectRecordingBot) {
      let timer = setTimeout(() => {
        setRecordingLoader(false)
        setRecordingStart(false)
        setDisconnectRecordingBot(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnectRecordingBot, isDisconnectRecordingBot])

  const dataRecordIcon = React.useMemo(() => {
    return {
      icons: [
        <Icon src={RiRecordCircleFill} width="1.17rem" height="1.17rem" />
      ],
      // if streaming is active or local user is nonRecordingUser show "Recording"
      names: [
        // OFF condition
        isRecordingLoader
          ? <LocalButtonLoader enabled={isDisconnectRecordingBot} />
          : isStreamingStarted
          ? t("Recording")
          : t("Record"),
        // ON condition
        isRecordingLoader
          ? <LocalButtonLoader enabled={isDisconnectRecordingBot} />
          : isNonRecordingUser
          ? t("Recording")
          : t("Stop recording")
      ]
    }
  }, [isRecordingLoader, t, isStreamingStarted, isNonRecordingUser, isDisconnectRecordingBot])

  return (
    // show tooltip when streaming is ON
    <Tooltip
      label={t("Whenever you stream, a recording will also be made")}
      placement="top"
      isDisabled={!isStreamingStarted}
    >
      <Box>
        <LocalButton
          id="6"
          names={dataRecordIcon.names}
          icons={dataRecordIcon.icons}
          enabled={isRecordingStart}
          isActive={isRecordingStart}
          setter={handleToggleRecord}
          isHidden={isButtonHidden(UserPermission.canRecordSessions)}
          isLast={isLast}
          isFirst={isFirst}
          isDisabled={isRecordingDisabled}
          isWithoutTitle={isWithoutTitle}
        />
      </Box>
    </Tooltip>
  )
}

export default RecordButton
